/**
 * BM Versioning MVI Intents
 *
 * Architecture: MVI Pattern - Intent Layer
 * Intents describe what happened, not what should happen.
 */

import type { TFeatureId } from "@/shared/types/feature";
import type { IFeatureHistory, IFeatureCommitInfo } from "@/shared/types/bm-versioning";
import type { IApiError } from "@/shared/types";

// ============================================================================
// Intent Types
// ============================================================================

export type TVersioningIntent =
  // Feature selection
  | { readonly type: "SET_CURRENT_FEATURE"; readonly featureId: TFeatureId | null }

  // Dirty state
  | { readonly type: "SET_DIRTY"; readonly isDirty: boolean }

  // History loading
  | { readonly type: "LOAD_HISTORY_START" }
  | { readonly type: "LOAD_HISTORY_SUCCESS"; readonly history: IFeatureHistory }
  | { readonly type: "LOAD_HISTORY_ERROR"; readonly error: IApiError }

  // Commit
  | { readonly type: "COMMIT_START" }
  | {
      readonly type: "COMMIT_SUCCESS";
      readonly newFeatureId: TFeatureId;
      readonly commitInfo: IFeatureCommitInfo;
    }
  | { readonly type: "COMMIT_ERROR"; readonly error: IApiError }

  // Checkout
  | { readonly type: "CHECKOUT_START"; readonly targetFeatureId: TFeatureId }
  | {
      readonly type: "CHECKOUT_SUCCESS";
      readonly newFeatureId: TFeatureId;
      readonly newFeatureName: string;
    }
  | { readonly type: "CHECKOUT_ERROR"; readonly error: IApiError }

  // UI
  | { readonly type: "TOGGLE_HISTORY_PANEL" }
  | { readonly type: "OPEN_COMMIT_DIALOG" }
  | { readonly type: "CLOSE_COMMIT_DIALOG" }
  | { readonly type: "SET_CHECKOUT_TARGET"; readonly targetFeatureId: TFeatureId | null }
  | { readonly type: "CLEAR_ERROR" };

// ============================================================================
// Intent Creators
// ============================================================================

export const versioningIntents = {
  setCurrentFeature: (featureId: TFeatureId | null): TVersioningIntent => ({
    type: "SET_CURRENT_FEATURE",
    featureId,
  }),

  setDirty: (isDirty: boolean): TVersioningIntent => ({
    type: "SET_DIRTY",
    isDirty,
  }),

  loadHistoryStart: (): TVersioningIntent => ({
    type: "LOAD_HISTORY_START",
  }),

  loadHistorySuccess: (history: IFeatureHistory): TVersioningIntent => ({
    type: "LOAD_HISTORY_SUCCESS",
    history,
  }),

  loadHistoryError: (error: IApiError): TVersioningIntent => ({
    type: "LOAD_HISTORY_ERROR",
    error,
  }),

  commitStart: (): TVersioningIntent => ({
    type: "COMMIT_START",
  }),

  commitSuccess: (
    newFeatureId: TFeatureId,
    commitInfo: IFeatureCommitInfo
  ): TVersioningIntent => ({
    type: "COMMIT_SUCCESS",
    newFeatureId,
    commitInfo,
  }),

  commitError: (error: IApiError): TVersioningIntent => ({
    type: "COMMIT_ERROR",
    error,
  }),

  checkoutStart: (targetFeatureId: TFeatureId): TVersioningIntent => ({
    type: "CHECKOUT_START",
    targetFeatureId,
  }),

  checkoutSuccess: (
    newFeatureId: TFeatureId,
    newFeatureName: string
  ): TVersioningIntent => ({
    type: "CHECKOUT_SUCCESS",
    newFeatureId,
    newFeatureName,
  }),

  checkoutError: (error: IApiError): TVersioningIntent => ({
    type: "CHECKOUT_ERROR",
    error,
  }),

  toggleHistoryPanel: (): TVersioningIntent => ({
    type: "TOGGLE_HISTORY_PANEL",
  }),

  openCommitDialog: (): TVersioningIntent => ({
    type: "OPEN_COMMIT_DIALOG",
  }),

  closeCommitDialog: (): TVersioningIntent => ({
    type: "CLOSE_COMMIT_DIALOG",
  }),

  setCheckoutTarget: (targetFeatureId: TFeatureId | null): TVersioningIntent => ({
    type: "SET_CHECKOUT_TARGET",
    targetFeatureId,
  }),

  clearError: (): TVersioningIntent => ({
    type: "CLEAR_ERROR",
  }),
} as const;
